import React from "react";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { APP_ICON_3_PATH } from "../routes";
import IconCaptureCanvas from "./IconCaptureCanvas";

/** App icon 3 canvas. */
export const APP_ICON_3_SIZE = 512;

/** Board fills the canvas; the seam doubles as the outer margin so spacing stays even. */
const TILE_GAP = 10;
const TILE_RADIUS = 30;

const TILE_FILL = {
    redOrange: "linear-gradient(150deg, #ff7043 0%, #f4502a 46%, #dd3a1c 100%)",
    cream: "linear-gradient(150deg, #fdfaf3 0%, #f3ecdd 52%, #e7decb 100%)",
    limeGreen: "linear-gradient(150deg, #bdea56 0%, #8fd23a 48%, #62b428 100%)",
    oceanBlue: "linear-gradient(150deg, #46bde2 0%, #1f86cf 50%, #1560b4 100%)",
};

/** Matte bevel: thin lit top edge, shaded floor, no specular streak. */
const TILE_BEVEL =
    "inset 0 2px 1px rgba(255,255,255,0.42), inset 2px 0 1px rgba(255,255,255,0.22), inset 0 -4px 8px rgba(15,23,42,0.16)";

/** Soft ambient light from the top-left, kept low so tiles stay matte. */
const AMBIENT_LIGHT =
    "radial-gradient(circle at 24% 18%, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.10) 42%, rgba(255,255,255,0) 68%)";

const TILE_SHADOW =
    "0 10px 20px rgba(15,23,42,0.13), 0 3px 6px rgba(15,23,42,0.10)";

function Tile({ fill, shadow = TILE_SHADOW, sx }) {
    return (
        <Box
            sx={{
                position: "relative",
                borderRadius: `${TILE_RADIUS}px`,
                background: fill,
                boxShadow: `${TILE_BEVEL}, ${shadow}`,
                overflow: "hidden",
                ...sx,
            }}
        >
            <Box sx={{ position: "absolute", inset: 0, background: AMBIENT_LIGHT }} />
        </Box>
    );
}

Tile.propTypes = {
    fill: PropTypes.string.isRequired,
    shadow: PropTypes.string,
    sx: PropTypes.object,
};

/**
 * App icon 3 — soft-3D matte 2×2 tile grid. The ocean-blue tile hovers 10°
 * off its dark recessed slot to read as a tile mid-slide.
 * Pass `transparent` for a Facebook PNG with no white canvas fill.
 */
export default function AppIcon3Page({
    transparent = false,
    routePath = APP_ICON_3_PATH,
    slug = "app-icon-3",
    fileName = "sliding-puzzle-quest-app-icon-3-512x512.png",
    frameId = "sliding-app-icon-3",
}) {
    return (
        <IconCaptureCanvas
            routePath={routePath}
            slug={slug}
            fileName={fileName}
            frameId={frameId}
            size={APP_ICON_3_SIZE}
            transparent={transparent}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: transparent ? "transparent" : "#ffffff",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gridTemplateRows: "1fr 1fr",
                        gap: `${TILE_GAP}px`,
                        padding: `${TILE_GAP}px`,
                        boxSizing: "border-box",
                    }}
                >
                    <Tile fill={TILE_FILL.redOrange} />
                    <Tile fill={TILE_FILL.cream} />
                    <Tile fill={TILE_FILL.limeGreen} />

                    {/* Recessed slot with the tile hovering off it */}
                    <Box sx={{ position: "relative" }}>
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: `${TILE_RADIUS}px`,
                                background:
                                    "linear-gradient(150deg, #131c2b 0%, #1e2839 58%, #2a3448 100%)",
                                boxShadow:
                                    "inset 0 7px 15px rgba(0,0,0,0.58), inset 0 -2px 6px rgba(148,163,184,0.16)",
                            }}
                        />
                        <Tile
                            fill={TILE_FILL.oceanBlue}
                            shadow="0 16px 28px rgba(8,20,45,0.34), 0 5px 10px rgba(8,20,45,0.24)"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                // Largest scale whose rotated corners still clear the canvas edge.
                                transform: "rotate(10deg) scale(0.92)",
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </IconCaptureCanvas>
    );
}

AppIcon3Page.propTypes = {
    transparent: PropTypes.bool,
    routePath: PropTypes.string,
    slug: PropTypes.string,
    fileName: PropTypes.string,
    frameId: PropTypes.string,
};
